import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Menu, Phone, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/logo'
import { ButtonLink, Container } from '@/components/generals/primitives'
import { contact, primaryNav } from '@/constants/site'

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        setOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = ''
        }
    }, [open])

    const isActive = (href: string): boolean => {
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <header className='sticky top-0 z-50 bg-ink'>
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-minium focus:px-4 focus:py-2 focus:text-sm focus:text-white'
            >
                Skip to content
            </a>

            <div className='hidden border-b border-ink-line lg:block'>
                <Container>
                    <div className='flex items-center justify-between py-2 font-mono text-[0.65rem] uppercase tracking-label text-ink-muted'>
                        <p>
                            Extensions, loft conversions and structural work across {contact.coverage}
                        </p>

                        <p className='flex items-center gap-6'>
                            <span>Mon to Fri 08:00 - 18:00</span>
                            <span aria-hidden='true' className='h-3 w-px bg-ink-line' />
                            <span>Free site visit and fixed written quotation</span>
                        </p>
                    </div>
                </Container>
            </div>

            <Container>
                <div className='flex items-center justify-between gap-6 py-4'>
                    <Logo variant='mark' />

                    <nav aria-label='Primary' className='hidden lg:block'>
                        <ul className='flex items-center gap-8'>
                            {primaryNav.map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={isActive(item.href) ? 'page' : undefined}
                                        className={cn(
                                            'relative py-2 text-sm font-medium transition-colors',
                                            isActive(item.href)
                                                ? 'text-paper'
                                                : 'text-ink-muted hover:text-paper'
                                        )}
                                    >
                                        {item.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute -bottom-0.5 left-0 h-px bg-minium transition-all duration-200 ease-out',
                                                isActive(item.href) ? 'w-full' : 'w-0'
                                            )}
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='hidden items-center gap-5 lg:flex'>
                        <a
                            href={contact.phoneHref}
                            className='flex items-center gap-2 text-sm font-semibold text-paper transition-colors hover:text-minium-tint'
                        >
                            <Phone aria-hidden='true' className='size-4 text-minium' />
                            {contact.phone}
                        </a>

                        <ButtonLink href='/contact' variant='primary' withArrow={false}>
                            Request a quotation
                        </ButtonLink>
                    </div>

                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        className='inline-flex items-center gap-2 border border-ink-line px-3 py-2 text-sm text-paper lg:hidden'
                    >
                        {open ? (
                            <X aria-hidden='true' className='size-5' />
                        ) : (
                            <Menu aria-hidden='true' className='size-5' />
                        )}
                        <span className='sr-only'>{open ? 'Close menu' : 'Open menu'}</span>
                        <span aria-hidden='true' className='font-mono text-[0.65rem] uppercase tracking-label'>
                            Menu
                        </span>
                    </button>
                </div>
            </Container>

            <div
                id='mobile-menu'
                hidden={!open}
                className='border-t border-ink-line bg-ink lg:hidden'
            >
                <Container>
                    <nav aria-label='Primary mobile' className='py-6'>
                        <ul className='flex flex-col'>
                            {primaryNav.map((item, index) => (
                                <li key={item.href} className='border-b border-ink-line'>
                                    <Link
                                        href={item.href}
                                        aria-current={isActive(item.href) ? 'page' : undefined}
                                        className='flex items-baseline gap-4 py-4 text-lg font-semibold text-paper'
                                    >
                                        <span className='font-mono text-[0.65rem] text-minium'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-6 flex flex-col gap-3'>
                            <ButtonLink href='/contact' variant='primary' size='lg'>
                                Request a quotation
                            </ButtonLink>

                            <ButtonLink
                                href={contact.phoneHref}
                                variant='outlineLight'
                                size='lg'
                                withArrow={false}
                            >
                                Call {contact.phone}
                            </ButtonLink>
                        </div>
                    </nav>
                </Container>
            </div>
        </header>
    )
}
